import { useMemo, useState } from 'react';
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { useRequestVolume } from '../../hooks';
import { tooltipStyle, labelStyle } from '../AnalyticsComponents/analyticsUtils';
import { cn } from '../../lib/utils';

const PERIODS = ['7D', '14D', '30D'];

function getRangeForPeriod(period) {
  const today = new Date();
  const startDate = new Date();
  startDate.setDate(today.getDate() - parseInt(period.slice(0, -1), 10));
  const formatDate = date => date.toISOString().split('T')[0];
  return { start_date: formatDate(startDate), end_date: formatDate(today) };
}

export function RequestsChart({ enabled = true }) {
  const [activePeriod, setActivePeriod] = useState('7D');
  const { start_date, end_date } = getRangeForPeriod(activePeriod);
  const { data: totalRequestsData, isPending } = useRequestVolume(start_date, end_date, { enabled });

  const chartData = useMemo(() => {
    if (!totalRequestsData?.per_day) return [];
    return totalRequestsData.per_day
      .map(item => {
        const dateObj = new Date(item.timestamp || item.date);
        return {
          date: new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' }).format(dateObj),
          requests: item.count || 0,
          _timestamp: dateObj.getTime(),
        };
      })
      .sort((a, b) => a._timestamp - b._timestamp)
      .map(({ _timestamp, ...rest }) => rest);
  }, [totalRequestsData?.per_day]);

  return (
    <div className="p-6 rounded-xl bg-white border border-shell-border">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-sm font-semibold text-ink">Request Volume</h3>
          <p className="text-xs text-mute mt-0.5">
            Daily requests over the last {activePeriod.slice(0, -1)} days
          </p>
        </div>
        <div className="flex gap-1">
          {PERIODS.map(period => (
            <button
              key={period}
              type="button"
              onClick={() => setActivePeriod(period)}
              className={cn(
                'text-xs px-2.5 py-1 rounded-md transition-colors',
                period === activePeriod
                  ? 'bg-primary text-white font-semibold'
                  : 'text-mute hover:text-ink hover:bg-paper',
              )}>
              {period}
            </button>
          ))}
        </div>
      </div>

      {isPending ? (
        <div className="h-[280px] rounded-lg bg-paper border border-shell-border animate-pulse" />
      ) : (
        <ResponsiveContainer width="100%" height={280}>
          <AreaChart data={chartData}>
            <defs>
              <linearGradient id="adminSuccessGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#10B981" stopOpacity={0.4} />
                <stop offset="100%" stopColor="#10B981" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#262626" vertical={false} />
            <XAxis dataKey="date" stroke="#707070" fontSize={11} tickLine={false} axisLine={false} />
            <YAxis
              stroke="#707070"
              fontSize={11}
              axisLine={false}
              tickLine={false}
              tickFormatter={v => (v >= 1000 ? `${(v / 1000).toFixed(1)}k` : v)}
            />
            <Tooltip contentStyle={tooltipStyle} labelStyle={labelStyle} />
            <Area
              type="monotone"
              dataKey="requests"
              name="Total Requests"
              stroke="#10B981"
              fill="url(#adminSuccessGradient)"
              strokeWidth={2}
            />
          </AreaChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}
